<template>
  <aside
    id="v5-profile"
    ref="el"
    class="v5-prof"
    :class="{ 'is-pinned': pinned }"
    :aria-label="t('v5_ui_shell.panel')"
    tabindex="-1"
  >
    <!-- Manage friends -->
    <template v-if="view === 'friends'">
      <div class="v5-pop__head">
        <button class="v5-ib" type="button" :aria-label="t('v5_ui_shell.back')" @click="view = 'main'">
          <ChevronLeftIcon class="i" aria-hidden="true" />
        </button>
        <h2>{{ t('v5_ui_shell.manage_friends') }}</h2>
      </div>
      <div class="v5-prof__grp">
        <h3>{{ t('v5_ui_shell.friend_code') }}</h3>
        <button class="v5-conn v5-prof__code" type="button" :aria-label="t('v5_ui_shell.copy_code')" @click="copyFriendCode">
          <span class="num" :class="{ 'v5-prof__blur': user.streamer_mode }">{{ user.friend_code }}</span>
          <CheckIcon v-if="codeCopied" class="i v5-good" aria-hidden="true" />
          <DocumentDuplicateIcon v-else class="i" aria-hidden="true" />
        </button>
        <span class="sr" role="status">{{ codeCopied ? t('v5_ui_shell.code_copied') : '' }}</span>
      </div>
      <div class="v5-prof__grp">
        <h3><label :for="`${uid}-fc`">{{ t('v5_ui_shell.add_friend') }}</label></h3>
        <form class="v5-prof__add" @submit.prevent="addFriend">
          <input :id="`${uid}-fc`" v-model="friendCodeInput" type="text" :placeholder="t('v5_ui_shell.friend_code_ph')" autocomplete="off">
          <button class="v5-btn" type="submit" :disabled="!friendCodeInput || addingFriend">{{ t('v5_ui_shell.add') }}</button>
        </form>
        <small v-if="friendMessage" class="v5-prof__msg" :class="friendMessageIsError ? 'v5-bad' : 'v5-good'" role="status">{{ friendMessage }}</small>
      </div>
      <div v-if="friendRequests.length" class="v5-prof__grp">
        <h3>{{ t('v5_ui_shell.requests', { n: friendRequests.length }) }}</h3>
        <ul class="v5-pop__rows">
          <li v-for="request in friendRequests" :key="request.id">
            <router-link class="v5-prof__who" :to="userTo(request.user)" @click="emit('close')">
              <img class="v5-avatar" :src="request.user.avatar || fallbackAvatar" alt="">
              <span class="v5-pop__tx"><b>{{ request.user.display_name }}</b></span>
            </router-link>
            <button class="v5-ib" type="button" :aria-label="t('v5_ui_shell.accept_request', { name: request.user.display_name })" @click="acceptRequest(request)">
              <CheckIcon class="i" aria-hidden="true" />
            </button>
            <button class="v5-ib" type="button" :aria-label="t('v5_ui_shell.decline_request', { name: request.user.display_name })" @click="removeFriendship(request.id)">
              <XMarkIcon class="i" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>
      <div class="v5-prof__grp">
        <h3>{{ t('v5_ui_shell.friends', { n: acceptedFriends.length }) }}</h3>
        <p v-if="!acceptedFriends.length" class="v5-prof__none">{{ t('v5_ui_shell.no_friends') }}</p>
        <ul v-else class="v5-pop__rows">
          <li v-for="f in acceptedFriends" :key="f.id">
            <router-link class="v5-prof__who" :to="userTo(f.friend)" @click="emit('close')">
              <img class="v5-avatar" :src="f.friend.avatar || fallbackAvatar" alt="">
              <span class="v5-pop__tx">
                <b>{{ f.friend.display_name }}</b>
                <span v-if="isOnline(f.friend)" class="v5-good" :title="statusOf(f.friend)">{{ statusOf(f.friend) }}</span>
              </span>
            </router-link>
            <button class="v5-ib" type="button" :aria-label="t('v5_ui_shell.remove_named', { name: f.friend.display_name })" @click="friendPendingRemoval = f">
              <MinusIcon class="i" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>
    </template>

    <!-- Main view -->
    <template v-else>
      <router-link class="v5-prof__tier" :to="upgradeLink" @click="emit('close')">
        <span><small>{{ t('v5_ui_shell.your_plan') }}</small><b>{{ t(`v5.shell.tier.${tier.key}`) }}</b></span>
        <span class="v5-lnk">{{ t(`v5_ui_shell.${tier.cta}`) }}</span>
      </router-link>
      <router-link v-if="user.is_beta_tester" class="v5-who__beta v5-prof__beta" to="/settings#beta-access" @click="emit('close')">
        <StarIcon class="i" aria-hidden="true" />{{ t('v5_ui_shell.beta') }}
      </router-link>
      <nav class="v5-prof__nav" :aria-label="t('v5_ui_shell.panel')">
        <router-link class="v5-nav" :to="profileLink" @click="emit('close')"><UserCircleIcon class="i" aria-hidden="true" />{{ t('v5.shell.profile') }}</router-link>
        <router-link v-if="user.access_level > 2" class="v5-nav" :to="teamLink" @click="emit('close')"><UserGroupIcon class="i" aria-hidden="true" />{{ t('v5.shell.team') }}</router-link>
        <router-link class="v5-nav" :to="settingsLink" @click="emit('close')"><Cog6ToothIcon class="i" aria-hidden="true" />{{ t('v5.shell.settings') }}</router-link>
        <router-link class="v5-nav" :to="friendCodesLink" @click="emit('close')">
          <GiftIcon class="i" aria-hidden="true" />{{ t('v5.shell.friend_codes') }}
          <span v-if="trialCodeCount > 0" class="v5-chip num v5-prof__count" :aria-label="t('v5_ui_shell.trial_codes_n', { n: trialCodeCount })">{{ trialCodeCount }}</span>
        </router-link>
        <button class="v5-nav" type="button" @click="view = 'friends'">
          <UsersIcon class="i" aria-hidden="true" />{{ t('v5_ui_shell.manage_friends') }}
          <span v-if="friendRequests.length" class="v5-chip num v5-prof__count is-red" :aria-label="t('v5_ui_shell.requests_n', { n: friendRequests.length })">{{ friendRequests.length }}</span>
        </button>
      </nav>

      <div v-if="partyMembers.length" class="v5-prof__grp">
        <h3>{{ t('v5_ui_shell.party', { n: partyMembers.length }) }}</h3>
        <ul class="v5-pop__rows">
          <li v-for="member in partyMembers" :key="member.uuid">
            <img class="v5-avatar" :src="member.avatar || fallbackAvatar" alt="">
            <span class="v5-pop__tx"><b>{{ member.username || t('v5_ui_shell.unnamed') }}{{ member.id === user.id ? ` ${t('v5_ui_shell.you')}` : '' }}</b></span>
            <template v-if="canRemoveMember(member)">
              <button v-if="member.id === user.id" class="v5-btn" type="button" @click="removePartyMember(member)">{{ t('v5_ui_shell.leave_party') }}</button>
              <button v-else class="v5-ib" type="button" :aria-label="t('v5_ui_shell.remove_from_party', { name: member.username || t('v5_ui_shell.unnamed') })" @click="removePartyMember(member)">
                <XMarkIcon class="i" aria-hidden="true" />
              </button>
            </template>
          </li>
        </ul>
      </div>

      <div class="v5-prof__grp">
        <h3>{{ t('v5_ui_shell.online', { n: onlineFriends.length }) }}</h3>
        <p v-if="!onlineFriends.length" class="v5-prof__none">{{ t('v5_ui_shell.no_online') }}</p>
        <ul v-else class="v5-pop__rows">
          <li v-for="f in onlineFriends" :key="f.id">
            <router-link class="v5-prof__who" :to="userTo(f.friend)" @click="emit('close')">
              <img class="v5-avatar" :src="f.friend.avatar || fallbackAvatar" alt="">
              <span class="v5-pop__tx"><b>{{ f.friend.display_name }}</b><span class="v5-good" :title="statusOf(f.friend)">{{ statusOf(f.friend) }}</span></span>
            </router-link>
            <button
              v-if="canUseMatchmaking && !isInParty(f.friend)"
              class="v5-ib"
              type="button"
              :disabled="invitedUuids.includes(f.friend.uuid)"
              :aria-label="t(invitedUuids.includes(f.friend.uuid) ? 'v5_ui_shell.invited' : 'v5_ui_shell.invite', { name: f.friend.display_name })"
              @click="inviteToParty(f.friend)"
            >
              <CheckIcon v-if="invitedUuids.includes(f.friend.uuid)" class="i v5-good" aria-hidden="true" />
              <PlusIcon v-else class="i" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>

      <div class="v5-prof__foot">
        <button class="v5-nav" type="button" @click="emit('theme')">
          <component :is="theme === 'dark' ? SunIcon : MoonIcon" class="i" aria-hidden="true" />{{ theme === 'dark' ? t('v5.shell.light_mode') : t('v5.shell.dark_mode') }}
        </button>
        <button v-if="canImpersonate" class="v5-nav" type="button" @click="impersonate"><EyeIcon class="i" aria-hidden="true" />{{ t('v5.shell.impersonate') }}</button>
        <button v-if="impersonating" class="v5-nav" type="button" @click="stopImpersonating"><EyeSlashIcon class="i" aria-hidden="true" />{{ t('v5.shell.stop_impersonating') }}</button>
        <router-link class="v5-nav" :to="classic"><ArrowUturnLeftIcon class="i" aria-hidden="true" />{{ t('v5.shell.classic') }}</router-link>
        <button class="v5-nav v5-nav--bad" type="button" @click="emit('sign-out')"><ArrowRightStartOnRectangleIcon class="i" aria-hidden="true" />{{ t('v5.shell.sign_out') }}</button>
      </div>
    </template>
  </aside>

  <V5Dialog v-if="friendPendingRemoval" :id="`${uid}-rm`" :title="t('v5_ui_shell.remove_friend')" @close="friendPendingRemoval = null">
    <p class="v5-dialog__text">{{ t('v5_ui_shell.confirm_remove', { name: friendPendingRemoval.friend?.display_name }) }}</p>
    <div class="v5-prof__acts">
      <button class="v5-btn" type="button" @click="friendPendingRemoval = null">{{ t('v5_ui_shell.keep') }}</button>
      <button class="v5-btn-p" type="button" :disabled="removingFriend" @click="confirmRemoveFriend">{{ t('v5_ui_shell.confirm_yes') }}</button>
    </div>
  </V5Dialog>
</template>

<script setup>
import {
  computed, onMounted, ref, useId,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  ArrowRightStartOnRectangleIcon, ArrowUturnLeftIcon, CheckIcon, ChevronLeftIcon, Cog6ToothIcon,
  DocumentDuplicateIcon, EyeIcon, EyeSlashIcon, GiftIcon, MinusIcon, MoonIcon, PlusIcon, StarIcon,
  SunIcon, UserCircleIcon, UserGroupIcon, UsersIcon, XMarkIcon,
} from '@heroicons/vue/24/outline';
import V5Dialog from './V5Dialog.vue';
import useV5Account from './useV5Account';
import friendStatus from './friendStatus';
import useProfileSidebar from '../../composables/useProfileSidebar';
import { v5OrClassic } from '../nav';
import fallbackAvatar from '../../assets/logos/refrag-avatar.svg';

// The profile sidebar (mockup nf-5) for matchmaking players: plan, links, party, friends and
// the avatar menu's entries. Same calls as the classic ProfileSidebar (useProfileSidebar).
// pinned: shown beside the page (Matchmaking, match room on wide screens), never closes.
const props = defineProps({
  user: { type: Object, required: true },
  theme: { type: String, required: true },
  classic: { type: String, required: true },
  pinned: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'theme', 'sign-out']);
const { t } = useI18n();
const router = useRouter();
const uid = useId();
const el = ref(null);

const {
  view, friendRequests, trialCodeCount, friendCodeInput, addingFriend, friendMessage,
  friendMessageIsError, codeCopied, invitedUuids, friendPendingRemoval, removingFriend,
  canUseMatchmaking, partyMembers, acceptedFriends, isOnline, onlineFriends, isInParty,
  canRemoveMember, addFriend, acceptRequest, removeFriendship, confirmRemoveFriend, inviteToParty,
  removePartyMember, copyFriendCode,
} = useProfileSidebar(props);
const {
  impersonating, canImpersonate, profileLink, teamLink, settingsLink, friendCodesLink,
  impersonate, stopImpersonating,
} = useV5Account(props);

// Public tier names; the call to action follows the classic banner (Manage for admins and teams)
const TIERS = {
  1: 'core', 2: 'prime', 3: 'team', 4: 'pro', 5: 'admin',
};
const tier = computed(() => ({
  key: TIERS[props.user.access_level] || 'free',
  cta: [3, 5].includes(props.user.access_level) ? 'manage' : 'upgrade',
}));
const upgradeLink = computed(() => v5OrClassic(router, '/v5/upgrade', '/upgrade'));
const userTo = (u) => v5OrClassic(router, `/v5/user/${u.uuid}`, `/user/${u.uuid}`);
const statusOf = (friend) => friendStatus(t, friend, props.user.uuid);

// Opened as an overlay: focus moves into it (the shell returns it to the avatar button)
onMounted(() => { if (!props.pinned) el.value?.focus(); });
</script>

<style>
/* Unscoped: the shell's pinned layout makes room for the panel */
.v5 { --prof: 280px; }
.v5-prof { position: fixed; top: var(--bar); right: 0; bottom: 0; z-index: 35; display: flex; flex-direction: column; width: min(var(--prof), 100vw); overflow-y: auto; border-left: 1px solid var(--rule); background: var(--surface); color: var(--fg); box-shadow: -12px 0 40px color-mix(in oklab, #000, transparent 70%); }
.v5-prof:focus { outline: none; }
.v5-prof.is-pinned { box-shadow: none; }
.v5-prof .v5-pop__head h2 { margin: 0 auto 0 -4px; }
.v5-prof__tier { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 10px; padding: 12px 14px; border: 1px solid var(--rule); border-radius: var(--r); background: var(--raised); color: var(--fg); text-decoration: none; }
.v5-prof__tier > span:first-child { display: grid; }
.v5-prof__tier small { color: var(--fg-2); font-size: var(--t-xs); }
.v5-prof__tier b { font-size: var(--t-lg); }
.v5-prof__tier:hover .v5-lnk { color: var(--fg); text-decoration: underline; text-underline-offset: 3px; }
.v5-prof__beta { margin: -4px 10px 4px; }
.v5-prof__nav { display: grid; gap: 1px; padding: 0 6px 10px; border-bottom: 1px solid var(--rule); }
.v5-prof__count { margin-left: auto; }
.v5-prof__count.is-red { background: var(--red); color: #fff; }
.v5-prof__grp { display: grid; gap: 6px; padding: 12px 0 4px; }
.v5-prof__grp + .v5-prof__grp { border-top: 1px solid var(--rule); }
.v5-prof__grp h3 { margin: 0; padding: 0 16px; color: var(--muted); font-size: var(--t-xs); font-weight: 600; }
.v5-prof__grp .v5-pop__rows { padding: 0 0 4px; }
.v5-prof__who { display: flex; flex: 1; align-items: center; gap: 10px; min-width: 0; color: inherit; text-decoration: none; }
.v5-prof__who:hover b { text-decoration: underline; text-underline-offset: 3px; }
.v5-prof .v5-pop__tx .v5-good { color: var(--good); }
.v5-prof__none { margin: 0; padding: 0 16px 8px; color: var(--muted); font-size: var(--t-sm); }
.v5-prof__code { justify-content: space-between; max-width: none; margin: 0 16px 8px; }
.v5-prof__blur { filter: blur(4px); }
.v5-prof__add { display: flex; gap: 6px; margin: 0 16px; }
.v5-prof__add input { flex: 1; min-width: 0; height: 36px; padding: 0 10px; border: 1px solid var(--rule); border-radius: var(--r); background: var(--bg); color: var(--fg); font: inherit; font-size: var(--t-sm); }
.v5-prof__add .v5-btn { height: 36px; } /* same height as the field; both 44px on phones */
.v5-prof__msg { margin: 0 16px 8px; font-size: var(--t-xs); }
.v5-prof__foot { display: grid; gap: 1px; margin-top: auto; padding: 6px; border-top: 1px solid var(--rule); }
.v5-prof__acts { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.v5-prof__acts > * { min-height: 44px; }
.v5-prof-scrim { position: fixed; inset: var(--bar) 0 0 0; z-index: 34; }
/* Pinned beside the page: the content makes room, the top bar keeps the full width */
.v5-app.is-pinned .v5-main { padding-right: var(--prof); }
.v5-app.is-pinned .v5-top {
  margin-right: calc(-1 * var(--prof));
  /* Percentage padding resolves against the page column, which is --prof narrower here: add it back
     so the bar's content sits where it does on every other page instead of jumping to the far right */
  padding-inline: max(24px, calc((100% + var(--prof) - var(--max)) / 2 + 24px));
}
@media (max-width: 60rem) {
  .v5-prof-scrim { background: color-mix(in oklab, var(--bg), transparent 30%); }
  .v5-prof .v5-ib { min-width: 44px; }
  .v5-prof .v5-btn, .v5-prof__add input, .v5-prof .v5-conn { min-height: 44px; }
}
</style>
